import { useSelector } from 'react-redux';

import { useApiQuery } from 'theSource/utils';
import { CategoryResponseDto } from './types';
import {
  CONFIGURATION_ERROR_CODE,
  GET_BENEFITS_QUERY_KEY,
  QUESTIONNAIRE_BENEFIT_CATEGORY,
} from './utils';
import { selectIsAdmin } from 'theSource/features';
import { selectEnrollmentApiError } from 'theSource/services';

export interface UseBenefitCategoryQueryProps {
  enrollmentContextType: string;
  enabled: boolean;
  lumosApiHost: string;
  keepPreviousData?: boolean;
  key?: string;
  category?: string;
  getParamCategory?: boolean;
  includeEligibleDependentsData?: boolean;
  orgPlanYearId?: number;
}

export const useBenefitCategoryQuery = ({
  enrollmentContextType,
  enabled,
  lumosApiHost,
  includeEligibleDependentsData = true,
  keepPreviousData = false,
  key = `${GET_BENEFITS_QUERY_KEY}-${enrollmentContextType}`,
  category = QUESTIONNAIRE_BENEFIT_CATEGORY,
  getParamCategory = false,
  orgPlanYearId = 0,
}: UseBenefitCategoryQueryProps) => {
  const isAdmin = useSelector(selectIsAdmin);
  const enrollmentApiError = useSelector(selectEnrollmentApiError);
  const params = {
    enrollment_context_type: enrollmentContextType,
    include_eligible_dependents_data: includeEligibleDependentsData,
    ...(getParamCategory ? { benefit_category: category } : {}),
  };

  if (orgPlanYearId > 0) {
    params['org_plan_year_id'] = orgPlanYearId;
  }

  const { data, isLoading, isFetching, isPreviousData } =
    useApiQuery<CategoryResponseDto>({
      queryKey: key,
      config: {
        baseURL: `${lumosApiHost}/v2/self_service`,
      },
      endpoint: '/benefits',
      enabled: enabled,
      params: params,
      keepPreviousData: keepPreviousData,
      showErrorSnackbar: true,
      getErrorMessage(error) {
        const data = error.response?.data as {
          details: string;
          message: string;
          error_code: string;
        }; // typescript error otherwise
        return data.error_code === CONFIGURATION_ERROR_CODE && isAdmin
          ? data?.details
          : enrollmentApiError;
      },
    });

  return {
    data,
    isLoading,
    isFetching,
    isPreviousData,
  };
};
